import { useCallback, useEffect, useState } from 'react'
import {
  IAllSalesTeam,
  IGroupedSalesTeam,
  ISalesTeamUser,
  reAssignRecordsOnDeActivate,
  setUserActiveRecordCount,
} from '@user/store/users'
import { useAppDispatch } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import { updateLoadingState } from '@sales-center/common'

interface Props {
  groupedSalesTeams: IGroupedSalesTeam[]
  allUnassigned: boolean
  userId: string
  usersByTeamId: Record<number, ISalesTeamUser[]>
  fetchTeamsUsers: (teamIds: number[]) => void
  setAllUnassigned: (value: boolean) => void
  setFormValid: (value: boolean) => void
  onClose: (value: boolean) => void
  onReAssignSuccess: () => void
}

export function useActions(props: Props) {
  const {
    groupedSalesTeams,
    allUnassigned,
    usersByTeamId,
    fetchTeamsUsers,
    setAllUnassigned,
    setFormValid,
    onClose,
    userId,
    onReAssignSuccess,
  } = props
  const dispatch = useAppDispatch()

  const [status, setStatus] = useState<
    'ideal' | 'pending' | 'success' | 'fail'
  >('ideal')

  const [reAssignedTeams, setReAssignedTeams] = useState<IGroupedSalesTeam[]>(
    [],
  )

  useEffect(() => {
    setReAssignedTeams(JSON.parse(JSON.stringify(groupedSalesTeams)))
  }, [groupedSalesTeams])

  const onChangeSalesTeam = useCallback(
    (
      salesTeam: IAllSalesTeam | null,
      branchIndex: number,
      salesTeamIndex: number,
    ) => {
      const newReAssignedTeams = [...reAssignedTeams]
      let selectedSalesTeam =
        newReAssignedTeams[branchIndex].filteredSalesTeams[salesTeamIndex]

      if (salesTeam) {
        // Make sure the newly chosen team's candidates are loaded.
        fetchTeamsUsers([salesTeam.id])
        const ownerExistsInSalesTeam = usersByTeamId[salesTeam.id]?.some(
          (user) => user.id === selectedSalesTeam.user?.id,
        )
        selectedSalesTeam = {
          ...selectedSalesTeam,
          ...salesTeam,
          user: ownerExistsInSalesTeam ? selectedSalesTeam.user : undefined,
        }
        //
      } else {
        selectedSalesTeam = {} as IAllSalesTeam
      }

      newReAssignedTeams[branchIndex].filteredSalesTeams[salesTeamIndex] =
        selectedSalesTeam

      setReAssignedTeams(JSON.parse(JSON.stringify(newReAssignedTeams)))
    },
    [reAssignedTeams, fetchTeamsUsers, usersByTeamId],
  )

  const onChangeOwner = useCallback(
    (
      user: ISalesTeamUser | null,
      branchIndex: number,
      salesTeamIndex: number,
    ) => {
      const newReAssignedTeams = [...reAssignedTeams]
      newReAssignedTeams[branchIndex].filteredSalesTeams[salesTeamIndex] = {
        ...newReAssignedTeams[branchIndex].filteredSalesTeams[salesTeamIndex],
        user: user || undefined,
      }

      setReAssignedTeams(JSON.parse(JSON.stringify(newReAssignedTeams)))
    },
    [reAssignedTeams],
  )

  const onChangeAllUnassigned = useCallback(() => {
    const newReAssignedTeams = [...reAssignedTeams]
    newReAssignedTeams.forEach((branch) => {
      branch.filteredSalesTeams.forEach((team) => {
        if (!allUnassigned) {
          team.user = {
            id: 'unassigned',
            fullName: 'Unassigned',
          }
        } else if (team.user?.id === 'unassigned') {
          team.user = undefined
        }
      })
    })
    setAllUnassigned(!allUnassigned)
    setAllUnassigned(!allUnassigned)
  }, [allUnassigned, reAssignedTeams, setAllUnassigned])

  const handleClose = useCallback(() => {
    onClose(false)
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    setStatus('pending')
    dispatch(updateLoadingState('visible'))
    const updatedValues = reAssignedTeams
      .map((branch, branchIndex) =>
        branch.filteredSalesTeams.map((team, teamIndex) => ({
          oldSalesTeamId:
            groupedSalesTeams[branchIndex].filteredSalesTeams[teamIndex].id,
          newSalesTeamId: team.id,
          newOwnerId: team.user?.id || 'unassigned',
        })),
      )
      .flat()

    try {
      const result = await dispatch(
        reAssignRecordsOnDeActivate({ updatedValues, userId }),
      )

      if (
        reAssignRecordsOnDeActivate.fulfilled.match(result) &&
        result.payload
      ) {
        setStatus('success')
        dispatch(
          setUserActiveRecordCount({
            id: userId,
            openLeadCount: 0,
            openOppCount: 0,
          }),
        )
        onClose(false)
        onReAssignSuccess()
        dispatch(updateLoadingState('hidden'))
      } else {
        setStatus('fail')
        dispatch(updateLoadingState('hidden'))
      }
    } catch (error) {
      dispatch(updateLoadingState('hidden'))
      setStatus('fail')
      toast.error(error.Message)
    }
  }, [
    dispatch,
    reAssignedTeams,
    groupedSalesTeams,
    userId,
    onClose,
    onReAssignSuccess,
  ])

  useEffect(() => {
    const allUsersUnassigned = reAssignedTeams.every((branch) =>
      branch.filteredSalesTeams.every((team) => team.user?.id === 'unassigned'),
    )

    const formValid = reAssignedTeams.every((branch) =>
      branch.filteredSalesTeams.every((team) => team.user !== undefined),
    )

    if (allUsersUnassigned !== allUnassigned) {
      setAllUnassigned(allUsersUnassigned)
    }

    setFormValid(formValid)
  }, [reAssignedTeams, allUnassigned, setAllUnassigned, setFormValid])

  return {
    onChangeSalesTeam,
    onChangeOwner,
    reAssignedTeams,
    onChangeAllUnassigned,
    handleClose,
    handleSubmit,
    reAssignStatus: status,
  }
}
